import React, { useState } from "react";
import { Download } from "lucide-react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import DataTable from "@/components/Datatable/Datatable";
import { Button } from "@/components/ui/button";
import ReportPeriodBar from "../shared/ReportPeriodBar";
import ReportFailureState from "../shared/ReportFailureState";
import { formatRunSummary, getDefaultPeriod } from "../shared/period";
import { useMonthlyReport } from "./hooks/useMonthlyReport";
import { monthlyReportColumns } from "./components/monthlyReportColumns";

const MonthlyReport: React.FC = () => {
  const defaults = getDefaultPeriod();
  const [year, setYear] = useState(defaults.year);
  const [month, setMonth] = useState(defaults.month);

  const {
    exportCsv,
    failed,
    handlePageChange,
    handleSortingChange,
    hasRun,
    loading,
    meta,
    pageIndex,
    pageRows,
    pageSize,
    sorting,
    totalCount,
    run,
  } = useMonthlyReport();

  const handleRun = () => {
    run(Number(year), Number(month));
  };

  return (
    <MainPageWrapper
      actions={
        <Button
          disabled={!hasRun || loading || totalCount === 0}
          onClick={exportCsv}
          variant="outline"
        >
          <Download className="w-4 h-4 mr-1.5" />
          Export CSV
        </Button>
      }
      title="Monthly Report"
    >
      <div className="space-y-6">
        <ReportPeriodBar
          loading={loading}
          month={month}
          onMonthChange={setMonth}
          onRun={handleRun}
          onYearChange={setYear}
          summary={
            hasRun && meta
              ? // Count what the table and CSV actually render (`totalCount` is
                // `sortedRows.length`) rather than `meta.count`, so the summary
                // can never disagree with the rows on screen.
                formatRunSummary(totalCount, "row", meta.monthLabel)
              : undefined
          }
          year={year}
        />

        {failed ? (
          <ReportFailureState onRetry={handleRun} />
        ) : hasRun ? (
          <DataTable
            columns={monthlyReportColumns}
            data={pageRows}
            loading={loading}
            noDataMessage="No scoreboard records found for the selected month."
            onPageChange={handlePageChange}
            onSortingChange={handleSortingChange}
            pageIndex={pageIndex}
            pageSize={pageSize}
            rowKeyExtractor={(_, index) => index}
            sorting={sorting}
            totalCount={totalCount}
          />
        ) : (
          <div className="rounded-md border border-dashed border-gray-300 dark:border-slate-700 p-10 text-center text-sm text-gray-500 dark:text-slate-400">
            Select a month and year, then click{" "}
            <span className="font-medium">Run Report</span> to view the
            scoreboard results.
          </div>
        )}
      </div>
    </MainPageWrapper>
  );
};

export default MonthlyReport;
